import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  ChartBar, Users, CurrencyDollar, TrendUp, Storefront, Trophy, ArrowsCounterClockwise, Megaphone, Robot,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import BillingHealthCard from "@/components/BillingHealthCard";

const fmtMoney = (n) =>
  Number(n || 0).toLocaleString("en-US", { style: "currency", currency: "USD" });
const fmtPct = (n) => `${(Number(n || 0) * 100).toFixed(1)}%`;

export default function AdminRevenue() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [blastBusy, setBlastBusy] = useState(false);
  const [blastStatus, setBlastStatus] = useState(null);
  const [gens, setGens] = useState(null);
  const [refunds, setRefunds] = useState([]);

  const load = async () => {
    setLoading(true);
    const calls = [
      ["overview", api.get("/admin/revenue/overview")],
      ["recent-generations", api.get("/admin/revenue/recent-generations?limit=25")],
      ["refunds", api.get("/admin/refunds?status=pending")],
    ];
    const results = await Promise.allSettled(calls.map(([, p]) => p));
    const failed = [];
    results.forEach((res, i) => {
      const name = calls[i][0];
      if (res.status === "fulfilled") {
        if (name === "overview") setData(res.value.data);
        else if (name === "recent-generations") setGens(res.value.data);
        else if (name === "refunds") setRefunds(res.value.data || []);
      } else {
        const err = res.reason;
        const detail = err?.response?.data?.detail || err?.message || "unknown error";
        failed.push(`${name}: ${detail}`);
        console.error(`[admin/revenue] ${name} failed`, err);
      }
    });
    if (failed.length) {
      toast.error(`Failed to load: ${failed.join(" · ")}`);
    }
    setLoading(false);
  };

  const decideRefund = async (id, decision, row) => {
    let payload = {};
    if (decision === "reject") {
      const notes = window.prompt("Optional note to send to the customer:") || "";
      payload = { admin_notes: notes };
    } else {
      const full = row?.amount_usd ?? 0;
      const raw = window.prompt(
        `Refund amount in USD (leave empty for the full $${full.toFixed(2)} refund):`,
        "",
      );
      if (raw === null) return; // user cancelled
      const trimmed = raw.trim();
      if (trimmed !== "") {
        const amt = parseFloat(trimmed);
        if (!Number.isFinite(amt) || amt <= 0) {
          toast.error("Refund amount must be a positive number.");
          return;
        }
        if (amt > full + 1e-6) {
          toast.error(`Cannot refund more than the original $${full.toFixed(2)}.`);
          return;
        }
        payload.amount_usd = amt;
      }
    }
    try {
      await api.post(`/admin/refunds/${id}/${decision}`, payload);
      toast.success(decision === "approve" ? "Refund processed via Stripe." : "Refund request closed.");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || `Could not ${decision} refund.`);
    }
  };

  const loadBlast = async () => {
    try {
      const { data } = await api.get("/admin/marketing/announce-founder/status");
      setBlastStatus(data);
    } catch {
      // status endpoint is non-critical
    }
  };

  const sendFounderBlast = async ({ dryRun }) => {
    const verb = dryRun ? "preview" : "send";
    if (!dryRun && !window.confirm(
      "Send the Founder's Plan announcement email to ALL trial users? This cannot be undone."
    )) return;
    setBlastBusy(true);
    try {
      const { data } = await api.post("/admin/marketing/announce-founder", {
        audience: "trial", dry_run: dryRun,
      });
      toast.success(
        dryRun
          ? `Preview: ${data.sent} would be sent (${data.skipped_already_sent} already received it).`
          : `Sent to ${data.sent} trial users.`
      );
      loadBlast();
    } catch (e) {
      toast.error(e.response?.data?.detail || `Failed to ${verb} blast`);
    } finally {
      setBlastBusy(false);
    }
  };

  useEffect(() => { load(); loadBlast(); }, []);

  if (loading) {
    return <div className="gf-overline" data-testid="admin-revenue-loading">Loading revenue dashboard…</div>;
  }

  if (!data) {
    return (
      <div className="max-w-2xl" data-testid="admin-revenue-error">
        <div className="gf-overline">Admin · Revenue</div>
        <h1 className="font-display font-black text-3xl tracking-tight mt-2">
          Couldn&apos;t load the revenue overview.
        </h1>
        <p className="text-zinc-600 mt-3 text-sm">
          The <code className="font-mono">/admin/revenue/overview</code> endpoint returned
          an error. Check the backend logs for the exact stack trace, then retry. The
          rest of the admin tooling (refunds, reconcile) is still available below.
        </p>
        <Button
          onClick={load}
          data-testid="admin-revenue-retry"
          className="gf-sharp bg-blue-700 hover:bg-blue-800 text-white font-bold uppercase tracking-wide mt-5"
        >
          <ArrowsCounterClockwise size={14} className="mr-1.5" /> Retry
        </Button>
        <div className="mt-10">
          <ReconcileSession />
          <RefundQueue requests={refunds} onDecide={decideRefund} />
        </div>
      </div>
    );
  }

  const { mrr, churn, funnel, templates, founder_plan, revenue_30d } = data;
  // Find chart max for sparkline scale.
  const maxAmt = Math.max(1, ...revenue_30d.series.map((s) => s.amount_usd));

  return (
    <div className="max-w-7xl" data-testid="admin-revenue-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="gf-overline">Admin · Revenue</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight mt-2">
            Revenue dashboard.
          </h1>
          <p className="text-zinc-600 mt-2 text-sm">
            MRR, churn, signup→paid funnel, template sales, and creator payouts. Live data.
          </p>
        </div>
        <Button
          onClick={load}
          variant="outline"
          data-testid="revenue-refresh-btn"
          className="gf-sharp border-zinc-300 uppercase font-bold tracking-wide"
        >
          <ArrowsCounterClockwise size={14} className="mr-1.5" /> Refresh
        </Button>
      </div>

      {/* KPI strip */}
      <div className="grid md:grid-cols-4 gap-4 mt-6">
        <KPI
          icon={CurrencyDollar}
          label="MRR"
          value={fmtMoney(mrr.usd)}
          sublabel={`${mrr.active_subscribers} active subscriber${mrr.active_subscribers === 1 ? "" : "s"}`}
          highlight
          testid="kpi-mrr"
        />
        <KPI
          icon={TrendUp}
          label="Signup → Paid"
          value={fmtPct(funnel.conversion_rate)}
          sublabel={`${funnel.total_paid_30d} of ${funnel.signups_30d} (30d)`}
          testid="kpi-conversion"
        />
        <KPI
          icon={Users}
          label="Churn rate (lifetime)"
          value={fmtPct(churn.rate_30d)}
          sublabel={`${churn.canceled_total} canceled`}
          testid="kpi-churn"
        />
        <KPI
          icon={Trophy}
          label="Founder slots"
          value={`${founder_plan.used} / ${founder_plan.limit}`}
          sublabel={`${founder_plan.remaining} remaining`}
          onAction={async () => {
            if (!window.confirm(
              "Reset the Founder's Plan counter back to 100 remaining?\n\n" +
              "Use this if test-mode purchases inflated the counter. Real live-mode " +
              "subscribers will still be honored on future decrements — this only " +
              "resets the display counter."
            )) return;
            try {
              const { data } = await api.post("/admin/founders/reset-counter");
              toast.success(`Reset ✓ — ${data.remaining} spots remaining`);
              load();
            } catch (e) {
              toast.error(e.response?.data?.detail || "Reset failed");
            }
          }}
          actionLabel="Reset"
          testid="kpi-founders"
        />
      </div>

      {/* 30-day revenue chart */}
      <div className="mt-6 border border-zinc-200 bg-white p-6" data-testid="revenue-chart-card">
        <div className="flex items-baseline justify-between">
          <div>
            <div className="gf-overline">Revenue · last 30 days</div>
            <div className="font-display font-bold text-3xl mt-1 tracking-tight">
              {fmtMoney(revenue_30d.total_usd)}
            </div>
          </div>
          <div className="text-xs font-mono text-zinc-500">peak ${maxAmt.toFixed(0)}</div>
        </div>
        <div
          className="mt-5 flex items-end gap-1 h-32 border-b border-zinc-200"
          data-testid="revenue-30d-chart"
        >
          {revenue_30d.series.map((s) => (
            <div
              key={s.date}
              className="flex-1 bg-blue-700 hover:bg-blue-800 transition-colors relative group"
              style={{ height: `${(s.amount_usd / maxAmt) * 100}%`, minHeight: s.amount_usd > 0 ? "2px" : "0" }}
              title={`${s.date}: ${fmtMoney(s.amount_usd)}`}
            />
          ))}
        </div>
        <div className="mt-2 flex justify-between text-[10px] text-zinc-400 font-mono">
          <span>{revenue_30d.series[0]?.date}</span>
          <span>{revenue_30d.series[revenue_30d.series.length - 1]?.date}</span>
        </div>
      </div>

      {/* Funnel breakdown */}
      <div className="grid md:grid-cols-2 gap-6 mt-6">
        <div className="border border-zinc-200 bg-white p-6" data-testid="funnel-card">
          <div className="gf-overline flex items-center gap-1.5">
            <ChartBar size={12} weight="bold" /> Signup → Paid funnel (30d)
          </div>
          <div className="mt-4 space-y-3">
            <FunnelRow label="Signups" value={funnel.signups_30d} base={funnel.signups_30d} />
            <FunnelRow label="Trial started" value={funnel.trial_started_30d} base={funnel.signups_30d} />
            <FunnelRow label="Subscription started" value={funnel.premium_started_30d} base={funnel.signups_30d} color="bg-emerald-600" />
            <FunnelRow label="30-day pass bought" value={funnel.pass_buyers_30d} base={funnel.signups_30d} color="bg-amber-500" />
          </div>
          <div className="mt-5 pt-4 border-t border-zinc-200 flex items-baseline justify-between">
            <div className="text-sm text-zinc-600">Conversion to paid</div>
            <div className="font-display font-bold text-2xl">{fmtPct(funnel.conversion_rate)}</div>
          </div>
        </div>

        <div className="border border-zinc-200 bg-white p-6" data-testid="templates-card">
          <div className="gf-overline flex items-center gap-1.5">
            <Storefront size={12} weight="bold" /> Marketplace
          </div>
          <div className="mt-4 grid grid-cols-3 gap-3">
            <MiniStat label="Purchases" value={templates.purchases} />
            <MiniStat label="Gross" value={fmtMoney(templates.gross_usd)} />
            <MiniStat label="Creator payouts" value={fmtMoney(templates.creator_payouts_usd)} accent="emerald" />
          </div>

          {templates.by_template.length > 0 && (
            <div className="mt-5 border-t border-zinc-200 pt-4">
              <div className="gf-overline">Top templates</div>
              <ul className="mt-2 space-y-2 text-sm">
                {templates.by_template.slice(0, 5).map((t) => (
                  <li key={t.template_id} className="flex items-baseline justify-between gap-2">
                    <span className="truncate max-w-xs">{t.title}</span>
                    <span className="font-mono text-xs text-zinc-500">{t.count} · {fmtMoney(t.gross_usd)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>

      <div className="text-xs text-zinc-400 font-mono mt-6">
        Generated {new Date(data.generated_at).toLocaleString()}
      </div>

      {/* Founder launch blast — fire-once campaign to existing trial users. */}
      <div
        className="mt-8 border-2 border-amber-500 bg-amber-50 p-6"
        data-testid="founder-blast-card"
      >
        <div className="flex items-start gap-4 flex-wrap">
          <Megaphone size={28} className="text-amber-700 mt-1" weight="fill" />
          <div className="flex-1 min-w-[200px]">
            <div className="gf-overline text-amber-700">Launch announcement</div>
            <div className="font-display font-bold text-xl mt-1 tracking-tight">
              Email all trial users about Founder's Plan
            </div>
            <p className="text-sm text-amber-900 mt-1 max-w-2xl">
              One-shot blast via Resend. Idempotent — re-running skips anyone already emailed.
              {blastStatus?.sent_count > 0 && (
                <> Already sent to <b>{blastStatus.sent_count}</b> user{blastStatus.sent_count === 1 ? "" : "s"}.</>
              )}
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={() => sendFounderBlast({ dryRun: true })}
              disabled={blastBusy}
              variant="outline"
              data-testid="founder-blast-preview-btn"
              className="gf-sharp border-amber-500 text-amber-800 uppercase font-bold tracking-wide"
            >
              Preview count
            </Button>
            <Button
              onClick={() => sendFounderBlast({ dryRun: false })}
              disabled={blastBusy}
              data-testid="founder-blast-send-btn"
              className="gf-sharp bg-amber-500 hover:bg-amber-600 text-white font-bold uppercase tracking-wide"
            >
              {blastBusy ? "Sending…" : "Send blast"}
            </Button>
          </div>
        </div>
      </div>

      {/* Recent AI generations — observability for production LLM health */}
      {gens && <RecentGenerations gens={gens} />}

      {/* Email healthcheck — confirm Resend is configured and the sender domain
          is verified by sending a one-off test. */}
      <EmailHealthcheck />

      {/* Manual reconcile — unstick paid customers whose webhook was lost */}
      <ReconcileSession />

      {/* Duplicate-subscription scanner — fixes "charged after cancel" cases */}
      <BillingHealthCard />

      {/* Pending refund requests — approve/reject inline */}
      <RefundQueue requests={refunds} onDecide={decideRefund} />
    </div>
  );
}

function RefundQueue({ requests, onDecide }) {
  return (
    <div className="mt-6 border border-zinc-200 bg-white p-6" data-testid="refund-queue-card">
      <div className="flex items-baseline justify-between">
        <div>
          <div className="gf-overline">Pending refunds</div>
          <h2 className="font-display font-bold text-xl mt-1 tracking-tight">
            {requests.length} awaiting your decision
          </h2>
        </div>
      </div>
      {requests.length === 0 ? (
        <p className="text-sm text-zinc-500 mt-4 font-mono">No pending refund requests.</p>
      ) : (
        <div className="mt-5 space-y-3">
          {requests.map((r) => (
            <div
              key={r.id}
              data-testid={`refund-request-${r.id}`}
              className="border border-amber-300 bg-amber-50 p-4 grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 items-start"
            >
              <div>
                <div className="font-mono text-xs text-zinc-600">
                  {new Date(r.created_at).toLocaleString()} · {r.user_email}
                </div>
                <div className="font-display font-bold text-lg mt-1">
                  ${r.amount_usd.toFixed(2)} {r.currency?.toUpperCase()}
                </div>
                <blockquote className="mt-2 text-sm text-zinc-700 border-l-4 border-amber-400 pl-3">
                  {r.reason}
                </blockquote>
              </div>
              <div className="flex gap-2 md:flex-col md:items-stretch">
                <Button
                  onClick={() => onDecide(r.id, "approve", r)}
                  data-testid={`refund-approve-${r.id}`}
                  className="gf-sharp bg-emerald-600 hover:bg-emerald-700 text-white font-bold uppercase tracking-wide"
                >
                  Approve & Refund
                </Button>
                <Button
                  variant="outline"
                  onClick={() => onDecide(r.id, "reject", r)}
                  data-testid={`refund-reject-${r.id}`}
                  className="gf-sharp border-zinc-300 uppercase font-bold tracking-wide"
                >
                  Reject
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function RecentGenerations({ gens }) {
  const { items, summary } = gens;
  const fmtMs = (ms) => (ms == null ? "—" : ms >= 1000 ? `${(ms / 1000).toFixed(1)}s` : `${ms}ms`);
  const fmtTime = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" }); }
    catch { return iso; }
  };
  const statusColor = (s) => s === "complete" ? "text-emerald-700" : s === "failed" ? "text-red-700" : "text-amber-700";

  return (
    <div className="mt-6 border border-zinc-200 bg-white p-6" data-testid="recent-generations-card">
      <div className="flex items-baseline justify-between flex-wrap gap-3">
        <div>
          <div className="gf-overline flex items-center gap-1.5">
            <Robot size={12} weight="bold" /> Recent AI generations
          </div>
          <h2 className="font-display font-bold text-xl mt-1 tracking-tight">
            Last {items.length} proposal jobs
          </h2>
        </div>
        <div className="flex gap-3" data-testid="gen-summary-stats">
          <MiniStat label="Success" value={`${Math.round(summary.success_rate * 100)}%`} accent="emerald" />
          <MiniStat label="Failed" value={summary.failed} />
          <MiniStat label="Pending" value={summary.pending} />
          <MiniStat label="P50" value={fmtMs(summary.latency_p50_ms)} />
          <MiniStat label="P95" value={fmtMs(summary.latency_p95_ms)} />
        </div>
      </div>

      {items.length === 0 ? (
        <p className="text-sm text-zinc-500 mt-6 font-mono">No generation jobs yet.</p>
      ) : (
        <div className="mt-5 overflow-x-auto">
          <table className="w-full text-sm" data-testid="recent-generations-table">
            <thead>
              <tr className="border-b border-zinc-200 text-[10px] uppercase tracking-wider text-zinc-500 font-mono">
                <th className="text-left py-2 pr-4">When</th>
                <th className="text-left py-2 pr-4">User</th>
                <th className="text-left py-2 pr-4">Grant #</th>
                <th className="text-left py-2 pr-4">Status</th>
                <th className="text-right py-2 pr-4">Latency</th>
                <th className="text-left py-2">Error</th>
              </tr>
            </thead>
            <tbody>
              {items.map((g) => (
                <tr key={g.job_id} className="border-b border-zinc-100" data-testid={`gen-row-${g.job_id}`}>
                  <td className="py-2 pr-4 font-mono text-xs text-zinc-700">{fmtTime(g.created_at)}</td>
                  <td className="py-2 pr-4 text-zinc-900 truncate max-w-[220px]" title={g.user_email}>{g.user_email}</td>
                  <td className="py-2 pr-4 font-mono text-xs text-zinc-600">{g.grant_number || "—"}</td>
                  <td className={`py-2 pr-4 font-mono text-xs uppercase font-bold ${statusColor(g.status)}`}>
                    {g.status}{g.error_status ? ` · ${g.error_status}` : ""}
                  </td>
                  <td className="py-2 pr-4 font-mono text-xs text-right text-zinc-700">{fmtMs(g.latency_ms)}</td>
                  <td className="py-2 text-xs text-zinc-500 truncate max-w-[360px]" title={g.error_detail || ""}>
                    {g.error_detail || ""}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function KPI({ icon: Icon, label, value, sublabel, highlight, testid, onAction, actionLabel }) {
  return (
    <div
      data-testid={testid}
      className={`border p-5 ${highlight ? "border-blue-700 bg-blue-50" : "border-zinc-200 bg-white"}`}
    >
      <div className="gf-overline flex items-center gap-1.5">
        {Icon && <Icon size={12} weight="bold" />} {label}
      </div>
      <div className={`font-display font-black text-3xl mt-2 tracking-tight ${highlight ? "text-blue-700" : ""}`}>
        {value}
      </div>
      {sublabel && <div className="text-xs text-zinc-500 mt-1 font-mono">{sublabel}</div>}
      {onAction && actionLabel && (
        <button
          onClick={onAction}
          data-testid={`${testid}-action`}
          className="mt-3 text-[10px] font-mono uppercase tracking-widest text-blue-700 hover:text-blue-900 hover:underline"
        >
          {actionLabel} →
        </button>
      )}
    </div>
  );
}

function FunnelRow({ label, value, base, color = "bg-blue-700" }) {
  const pct = base > 0 ? (value / base) * 100 : 0;
  return (
    <div>
      <div className="flex items-baseline justify-between text-sm">
        <span>{label}</span>
        <span className="font-mono text-xs text-zinc-500">{value}</span>
      </div>
      <div className="h-2 bg-zinc-100 mt-1">
        <div className={color} style={{ height: "100%", width: `${pct}%` }} />
      </div>
    </div>
  );
}

function MiniStat({ label, value, accent }) {
  return (
    <div className="border border-zinc-200 p-3">
      <div className="text-[10px] text-zinc-500 uppercase tracking-wider font-mono">{label}</div>
      <div className={`font-display font-bold text-lg mt-1 ${accent === "emerald" ? "text-emerald-700" : ""}`}>
        {value}
      </div>
    </div>
  );
}

function ReconcileSession() {
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    const val = input.trim();
    if (!val) return toast.error("Enter a user email or Stripe session id.");
    const isEmail = val.includes("@");
    const isSession = val.startsWith("cs_");
    if (!isEmail && !isSession) {
      return toast.error("Enter a user email (preferred) or a Stripe session id starting with cs_.");
    }
    setBusy(true);
    setResult(null);
    try {
      const url = isEmail
        ? `/admin/billing/reconcile-user/${encodeURIComponent(val.toLowerCase())}`
        : `/admin/billing/reconcile-session/${val}`;
      const { data } = await api.post(url);
      setResult(data);
      const ok = isEmail ? data.reconciled_any : data.reconciled;
      if (ok) toast.success("Reconciled. The user's account is now active.");
      else toast.warning(data.reason || "Nothing to reconcile — see details below.");
    } catch (e) {
      const msg = e.response?.data?.detail || "Failed to reconcile.";
      setResult({ error: msg });
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-6 border border-zinc-200 bg-white p-6" data-testid="reconcile-session-card">
      <div className="gf-overline">Stripe · Manual Reconcile</div>
      <h2 className="font-display font-bold text-xl mt-1 tracking-tight">
        Unstick a paid customer.
      </h2>
      <p className="text-sm text-zinc-600 mt-2 max-w-2xl">
        Enter the customer&apos;s <strong>email</strong> (one-click — we&apos;ll find their
        unfulfilled transaction automatically) or paste a Stripe Checkout session id
        (<code className="font-mono text-xs">cs_...</code>) for surgical control.
      </p>
      <div className="mt-4 flex flex-col sm:flex-row gap-2">
        <input
          data-testid="reconcile-session-input"
          type="text"
          placeholder="kimberly@example.com  —or—  cs_live_a1B2c3..."
          value={input}
          onChange={(e) => setInput(e.target.value)}
          className="flex-1 border border-zinc-300 px-3 py-2 font-mono text-sm focus:outline-none focus:border-blue-700"
          disabled={busy}
          onKeyDown={(e) => { if (e.key === "Enter" && !busy) run(); }}
        />
        <Button
          onClick={run}
          disabled={busy}
          data-testid="reconcile-session-btn"
          className="gf-sharp bg-blue-700 hover:bg-blue-800 text-white font-bold uppercase tracking-wide"
        >
          {busy ? "Reconciling…" : "Reconcile"}
        </Button>
      </div>
      {result && (
        <pre
          data-testid="reconcile-session-result"
          className="mt-4 bg-zinc-50 border border-zinc-200 p-3 text-xs font-mono overflow-x-auto"
        >
{JSON.stringify(result, null, 2)}
        </pre>
      )}
    </div>
  );
}


function EmailHealthcheck() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    const to = email.trim();
    if (!to || !to.includes("@")) {
      return toast.error("Enter a valid recipient email.");
    }
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.post("/admin/marketing/test-email", { to });
      setResult(data);
      toast.success(`Sent ✓ — check ${to}'s inbox (and spam folder the first time).`, {
        duration: 8000,
      });
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || "Test send failed.";
      setResult({ error: msg });
      toast.error(msg, { duration: 15000 });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-6 border border-zinc-200 bg-white p-6" data-testid="email-healthcheck-card">
      <div className="gf-overline">Resend · Email Healthcheck</div>
      <h2 className="font-display font-bold text-xl mt-1 tracking-tight">
        Verify email delivery is working.
      </h2>
      <p className="text-sm text-zinc-600 mt-2 max-w-2xl">
        Sends a one-off test email through Resend using your configured{" "}
        <code className="font-mono text-xs">SENDER_EMAIL</code>. If Resend rejects
        the request (e.g. domain not verified), you&apos;ll see the exact reason
        in the response below.
      </p>
      <div className="mt-4 flex flex-col sm:flex-row gap-2">
        <input
          data-testid="test-email-input"
          type="email"
          placeholder="you@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !busy) run(); }}
          className="flex-1 border border-zinc-300 px-3 py-2 font-mono text-sm focus:outline-none focus:border-blue-700"
          disabled={busy}
        />
        <Button
          onClick={run}
          disabled={busy}
          data-testid="test-email-send-btn"
          className="gf-sharp bg-blue-700 hover:bg-blue-800 text-white font-bold uppercase tracking-wide"
        >
          {busy ? "Sending…" : "Send test"}
        </Button>
      </div>
      {result && (
        <pre
          data-testid="test-email-result"
          className="mt-4 bg-zinc-50 border border-zinc-200 p-3 text-xs font-mono overflow-x-auto whitespace-pre-wrap"
        >
{JSON.stringify(result, null, 2)}
        </pre>
      )}
    </div>
  );
}

